import styled from "@emotion/styled";
import { useMemo, useState, type MouseEvent } from "react";

import {
  classNames,
  ConfirmDestructiveButton,
  IconButton,
  PanelDragItem,
  SkiliticsIcon,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import type { DesignerCondition } from "../../types";
import { CONDITION_TYPE, TYPE_INSTANCE } from "../constants";
import type { DraggableData, DroppableData } from "../dnd/dndSchemas";
import { SortableItem } from "../dnd/SortableItem";
import { useResponseEditorContext } from "../response-editor";
import { ConditionDesigner } from "./ConditionDesigner";
import { DROPZONE_PADDING } from "./constants";
import { EditPopover } from "./EditPopover";
import { EffectConnector } from "./EffectConnector";
import { ConditionOrEffectIconContainer, EditButtonContainer } from "./shared";
import { isConditionAllowedChildren } from "./utils";

const ConditionContainer = styled.div`
  // Conditions at top level.
  padding-right: ${DROPZONE_PADDING * 2}px;
  padding-left: ${DROPZONE_PADDING}px;
  width: 100%;

  // Group container.
  &.isGroup {
    padding: 0;
  }

  // Conditions nested inside group containers.
  & & {
    padding-right: ${DROPZONE_PADDING}px;
    padding-left: 0;
    // Group container nested inside group container.
    &.isGroup {
      padding: 0;
    }
  }
`;

const ConditionConnector = styled(EffectConnector)`
  padding-left: ${DROPZONE_PADDING - 0.5}px;
  .isGroup & {
    padding-left: 10px;
    height: 20px;
  }
`;

const GroupConditionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 28px;
  width: 100%;
  > ${EditButtonContainer} {
    margin-top: 24px;
  }
`;

const ConditionDragItem = styled(PanelDragItem)`
  display: grid;
  grid-template-columns: 30px 1fr 28px;
  gap: 10px;
  background: ${({ theme }) => theme.surfaceVariant};
`;

export type ChildConditionEditorProps = {
  condition: DesignerCondition;
  index: number;
  group?: string;
  dropzoneDisabled?: boolean;
  onRemoveCondition: (id: DesignerCondition["id"]) => void;
};

export const ChildConditionEditor = ({
  condition,
  index,
  group,
  dropzoneDisabled,
  onRemoveCondition,
}: ChildConditionEditorProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { disabledDropzoneIds } = useResponseEditorContext();

  const isDropDisabled = useMemo(() => {
    if (dropzoneDisabled || disabledDropzoneIds.includes(condition.id)) return true;
  }, [condition.id, disabledDropzoneIds, dropzoneDisabled]);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const allowedChildren = isConditionAllowedChildren(condition);

  const handlePopoverClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveCondition = () => {
    onRemoveCondition(condition.id);
    handlePopoverClose();
  };

  return (
    <>
      <ConditionConnector lineEnd={condition.isValid ? "arrow" : undefined} />
      <ConditionContainer className={classNames({ isGroup: allowedChildren })}>
        <SortableItem
          key={`${condition.id}`}
          id={`${condition.id}`}
          index={index}
          group={group}
          disabled={!!isDropDisabled}
          data={
            {
              title: condition.spec.title,
              rule: {
                type: TYPE_INSTANCE,
                ruleType: CONDITION_TYPE,
                id: condition.id,
                typeId: condition.builder.typeId,
              },
            } satisfies DroppableData & DraggableData
          }
          accepts={{ "rule.ruleType": [CONDITION_TYPE] }}
          color="secondary"
        >
          {
            // Show summary view unless condition is being edited, or, it is a utility condition
            allowedChildren ? (
              <GroupConditionContainer>
                <ConditionDesigner condition={condition} />
                <EditButtonContainer>
                  <ConfirmDestructiveButton
                    header={formatMessage("deleteHeader")}
                    message={formatMessage("deleteContent")}
                    onDestroy={handleRemoveCondition}
                    color="error"
                  />
                </EditButtonContainer>
              </GroupConditionContainer>
            ) : (
              <ConditionDragItem
                onClick={handlePopoverClick}
                key={condition.id}
                className={classNames({
                  purple: true,
                  disabled: !condition.isValid,
                  active: Boolean(anchorEl),
                })}
              >
                <ConditionOrEffectIconContainer>
                  <SkiliticsIcon icon={!condition.isValid ? "hidden-off" : "condition"} />
                </ConditionOrEffectIconContainer>
                <div>
                  <ConditionDesigner condition={condition} view="summary" />
                </div>
                <EditButtonContainer onClick={handlePopoverClick}>
                  <IconButton icon="pencil-alt" data-test="edit-button" />
                </EditButtonContainer>
              </ConditionDragItem>
            )
          }
        </SortableItem>
        {!allowedChildren && (
          <EditPopover
            id={`edit-condition-popover-${condition.id}`}
            title={condition.spec.title}
            description={condition.spec.description}
            conditionOrEffect="condition"
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            onRemove={handleRemoveCondition}
          >
            <ConditionDesigner condition={condition} />
          </EditPopover>
        )}
      </ConditionContainer>
    </>
  );
};

const formatStrings = {
  deleteHeader: {
    en: "Delete Condition Group",
  },
  deleteContent: {
    en: `Are you sure you want to delete this group of conditions? This cannot be undone.`,
  },
};
